import { useCallback, useRef, useEffect } from 'react';
import { Dayjs } from 'dayjs';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { useAutosave } from '../../../hooks/useAutosave';

const GET_DAY_AND_SECTIONS = gql`
  query GetDayAndSections($date: String!) {
    getDay(date: $date) {
      id
      date
      customFieldValues {
        fieldId
        value
      }
    }
    getCustomFieldSections {
      id
      title
      orderIndex
      fields {
        id
        name
        type
        options
        published
      }
    }
  }
`;

const UPDATE_DAY = gql`
  mutation UpdateDay($date: String!, $input: DayInput!) {
    updateDay(date: $date, input: $input) {
      id
      customFieldValues {
        fieldId
        value
      }
    }
  }
`;

const buildDefaultValues = (sections: any[], dayValues: any[]) => {
  const defaults: Record<string, any> = {};
  const allFields: any[] = [];

  sections.forEach((sec) => {
    (sec.fields || []).filter((f: any) => f.published !== false).forEach((field: any) => {
      allFields.push(field);
      if (field.type === 'NUMBER') {
        defaults[field.id] = 0;
      } else if (field.type === 'BOOLEAN') {
        defaults[field.id] = false;
      } else {
        defaults[field.id] = '';
      }
    });
  });

  if (dayValues) {
    dayValues.forEach((item: any) => {
      const fieldDef = allFields.find((f) => f.id === item.fieldId);
      if (fieldDef) {
        if (fieldDef.type === 'NUMBER') {
          const val = parseFloat(item.value);
          defaults[item.fieldId] = isNaN(val) ? 0 : val;
        } else if (fieldDef.type === 'BOOLEAN') {
          defaults[item.fieldId] = item.value === 'true';
        } else {
          defaults[item.fieldId] = item.value || '';
        }
      }
    });
  }

  return defaults;
};

interface UseDayMetricsFormResult {
  data: any;
  loading: boolean;
  error: any;
  methods: ReturnType<typeof useForm<Record<string, any>>>;
  status: any;
  sections: any[];
}

export const useDayMetricsForm = (selectedDate: Dayjs): UseDayMetricsFormResult => {
  const dateStr = selectedDate.format('YYYY-MM-DD');

  const { data, loading, error } = useQuery(GET_DAY_AND_SECTIONS, {
    variables: { date: dateStr },
  });

  const [updateDay] = useMutation(UPDATE_DAY);

  const methods = useForm<Record<string, any>>({
    defaultValues: {},
  });

  const { reset, watch } = methods;
  const formValues = watch();

  const saveMetrics = useCallback(async (formData: Record<string, any>) => {
    try {
      const customFieldValues = Object.entries(formData).map(([key, val]) => ({
        fieldId: key,
        value: val !== null && val !== undefined ? String(val) : '',
      }));

      await updateDay({
        variables: {
          date: dateStr,
          input: { customFieldValues },
        },
      });
    } catch (error) {
      console.error('Error updating metrics:', error);
      throw error;
    }
  }, [dateStr, updateDay]);

  const { status, setStatus, setManualData } = useAutosave(formValues, saveMetrics);

  const lastDateRef = useRef<string | null>(null);

  useEffect(() => {
    if (data?.getDay && data?.getCustomFieldSections && dateStr !== lastDateRef.current) {
      lastDateRef.current = dateStr;
      const defaults = buildDefaultValues(data.getCustomFieldSections, data.getDay.customFieldValues);
      reset(defaults);
      setManualData(defaults);
    }
  }, [data, dateStr, reset, setManualData]);

  const sections = data?.getCustomFieldSections || [];

  return { data, loading, error, methods, status, sections };
};
